import TileMinedLogic from "../../../logic/TileMinedLogic";
import { Settings } from "../../../Settings";
import Character from "../../character/Character";
import PartyCharacter from "../../character/PartyCharacter";
import GridTile from "../../grid/GridTile";
import VectorField from "../../grid/VectorField";
import WorldGrid from "../../grid/WorldGrid";
import Vector from "../../math/Vector";
import GameTime from "../../time/GameTime";
import NumberValue from "../../value/NumberValue";
import LineBasedSkill from "../LineBasedSkill";
import SkillUtil from "../SkillUtil";

const INVALID_TARGET_RADIANS = 1000;

const FRAME_STEP_RADIANS = Math.PI / 32;

const TWO_PI = Math.PI * 2;

const RADIAN_THRESHOLD = Math.PI;

export default class Laser extends LineBasedSkill {
    /**
     * @param {WorldGrid} worldGrid
     * @param {GameTime} gameTime
     * @param {VectorField} vectorField
     * @param {TileMinedLogic} tileMinedLogic
     * @param {NumberValue} laserRangeUpgrades
     */
    constructor(worldGrid, gameTime, vectorField, tileMinedLogic, laserRangeUpgrades) {
        super(worldGrid);

        /**
         * @constant
         * @type {VectorField}
         */
        this.vectorField = vectorField;

        /**
         * @constant
         * @type {GameTime}
         */
        this.gameTime = gameTime;

        /**
         * @constant
         * @type {TileMinedLogic}
         */
        this.tileMinedLogic = tileMinedLogic;

        /**
         * @constant
         * @type {NumberValue}
         */
        this.laserRangeUpgrades = laserRangeUpgrades;

        /**
         * @type {GridTile}
         */
        this.prevLaserTargetTile = null;

        /**
         * @type {number}
         */
        this.actualRadians = INVALID_TARGET_RADIANS;

        /**
         * @type {boolean}
         */
        this.skillActive = false;

        /**
         * @type {boolean}
         */
        this.laserVisible = false;

        /**
         * @type {number}
         */
        this.prevTurn = 0;
    }

    onSkillActivation() {
        this.actualRadians = INVALID_TARGET_RADIANS;
        this.skillActive = true;
        this.prevLaserTargetTile = null;
    }

    onSkillDeactivation() {
        this.actualRadians = INVALID_TARGET_RADIANS;
        this.skillActive = false;
        this.laserVisible = false;
        this.prevLaserTargetTile = null;
    }

    /**
     * @param {PartyCharacter} miner
     * @param {number} frameTimeRatio
     */
    updateForFrame(miner, frameTimeRatio) {
        if (!miner.position.tile) {
            this.laserVisible = false;
            this.actualRadians = INVALID_TARGET_RADIANS;
            return;
        }

        // Move the center from the origin to the center of the tile.
        this.startPoint.copy(miner.position.worldCoordinateOrigin);
        this.startPoint.addXY(Settings.tile.halfSize, Settings.tile.halfSize);

        // The default range is 1 tile, but we don't want to count the tile the miner is standing in. Add 1.
        const pixelRange = (Settings.skill.laser.minRange + (Settings.skill.laser.tilesPerUpgrade * this.laserRangeUpgrades.getValue())) * Settings.tile.size;

        // Figure out the radians
        const lineRadians = this.chooseLaserRadians(miner, pixelRange, this.startPoint);
        if (lineRadians === INVALID_TARGET_RADIANS) {
            this.laserVisible = false;
            this.actualRadians = INVALID_TARGET_RADIANS;
            return;
        }

        if (this.actualRadians === INVALID_TARGET_RADIANS) {
            this.actualRadians = lineRadians;
        }
        else {
            this.actualRadians = this.updateRadians(this.actualRadians, lineRadians, frameTimeRatio);
        }

        // Project along the line to find what tile is hit
        const laserTile = this.findFirstClosedTileOnLine(this.startPoint, this.actualRadians, pixelRange);
        if (!laserTile) {
            this.laserVisible = false;    
            this.actualRadians = INVALID_TARGET_RADIANS;
            return;
        }

        // If we are hitting it with a laser, then we should be able to see it.
        laserTile.markTileAsCurrentlyVisible();

        // Set the start/end positions of the laser (for the benefit of the canvas panel)
        this.endPoint.copy(laserTile.origin);
        this.endPoint.addXY(Settings.tile.halfSize, Settings.tile.halfSize);

        if (!laserTile.open) {
            this.tileMinedLogic.laserTileForFrame(laserTile, frameTimeRatio);
        }

        this.laserVisible = true;
    }

    /**
     * @private
     * @param {number} currentRadians 
     * @param {number} newRadians 
     * @param {number} frameTimeRatio 
     * @returns {number}
     */
    updateRadians(currentRadians, newRadians, frameTimeRatio) {
        //const delta = currentRadians - newRadians;

        const radiansStep = FRAME_STEP_RADIANS * frameTimeRatio;

        const deltaPos = this.getRadiansPositiveDirection(currentRadians, newRadians);
        const deltaNeg = this.getRadiansNegativeDirection(currentRadians, newRadians);

        // if (deltaPos < 0 || deltaNeg < 0) {
        //     console.log('laser deltaPos=' + deltaPos + ' deltaNeg=' + deltaNeg + ' curr=' + currentRadians + ' new=' + newRadians);
        // }

        if (deltaPos < deltaNeg) {
            // If the change is tiny or huge, just use the new radians.
            if (deltaPos < radiansStep || deltaPos >= RADIAN_THRESHOLD) {
                return newRadians;
            }

            return this.clamp(currentRadians + radiansStep);
        }
        else {
            // If the change is tiny or huge, just use the new radians.
            if (deltaNeg < radiansStep || deltaNeg >= RADIAN_THRESHOLD) {
                return newRadians;
            }

            return this.clamp(currentRadians - radiansStep);
        }
    }

    /**
     * @private
     * @param {number} radians 
     * @returns {number}
     */
    clamp(radians) {
        if (radians < 0) {
            return radians + TWO_PI;
        }
        else if (radians > TWO_PI) {
            return radians - TWO_PI;
        }
        else {
            return radians;
        }
    }

    /**
     * Returns positive amount of radians.
     * @private
     * @param {number} currentRadians 
     * @param {number} newRadians 
     * @returns {number}
     */
    getRadiansPositiveDirection(currentRadians, newRadians) {
        if (newRadians < currentRadians) {
            // rotate to 2PI and then rotate to newRadians
            return (TWO_PI - currentRadians) + newRadians;
        }
        else {
            return newRadians - currentRadians;
        }
    }

    /**
     * Returns positive amount of radians.
     * @private
     * @param {number} currentRadians 
     * @param {number} newRadians 
     * @returns {number}
     */
    getRadiansNegativeDirection(currentRadians, newRadians) {
        if (newRadians < currentRadians) {
            return currentRadians - newRadians;
        }
        else {
            return (TWO_PI - newRadians) + currentRadians;
        }
    }

    /**
     * @private
     * @param {PartyCharacter} miner
     * @param {number} pixelRange
     * @param {Vector} startPoint This is at the center of the tile
     * @returns {number}
     */
    chooseLaserRadians(miner, pixelRange, startPoint) {
        const laserTargetTile = this.chooseLaserTargetTile(miner, pixelRange);
        if (laserTargetTile) {

            ///this.debugTargetPosition.copy(laserTargetTile.origin);

            this.workingPosition.copy(laserTargetTile.origin);
            this.workingPosition.addXY(Settings.tile.halfSize, Settings.tile.halfSize);

            return this.calculateAngle(startPoint, this.workingPosition);
        }
        else {
            return INVALID_TARGET_RADIANS;
        }
    }

    /**
     * @private
     * @param {Character} miner 
     * @param {number} pixelRange
     */
    chooseLaserTargetTile(miner, pixelRange) {
        const pixelRange2 = pixelRange * pixelRange;
        const minerPosition = miner.position.worldCoordinateOrigin;

        const priorityTile2 = SkillUtil.findClosestTile(minerPosition, this.vectorField.priorityMiningCandidates);
        if (priorityTile2) {
            this.prevLaserTargetTile = priorityTile2;
            return priorityTile2;
        }

        const currencyTile2 = SkillUtil.findClosestTile(minerPosition, this.vectorField.currencyMiningCandidates);
        if (currencyTile2) {
            this.prevLaserTargetTile = currencyTile2;
            return currencyTile2;
        }
        
        // const priorityTile = SkillUtil.findClosestTileInRange(minerPosition, this.vectorField.priorityMiningCandidates, pixelRange2);
        // if (priorityTile) {
        //     this.prevLaserTargetTile = priorityTile;
        //     return priorityTile;
        // }

        // const currencyTile = SkillUtil.findClosestTileInRange(minerPosition, this.vectorField.currencyMiningCandidates, pixelRange2);
        // if (currencyTile) {
        //     this.prevLaserTargetTile = currencyTile;
        //     return currencyTile;
        // }

        const commonTile = SkillUtil.findClosestTileInRange(minerPosition, this.vectorField.commonMiningCandidates, pixelRange2);
        if (commonTile) {
            this.prevLaserTargetTile = commonTile;
            return commonTile;
        }

        // Just use the mine target as a destination tile.
        const mineTarget = miner.target.mineTile;
        if (mineTarget) {
            this.prevLaserTargetTile = mineTarget;
            return mineTarget;
        }

        const targetTile = miner.target.targetTile;
        if (targetTile && miner.position.tile !== targetTile) {
            this.prevLaserTargetTile = targetTile;
            return targetTile;
        }

        if (this.prevLaserTargetTile && !this.prevLaserTargetTile.open) {
            return this.prevLaserTargetTile;
        }

        this.prevLaserTargetTile = null;
        return null;
    }

    // /**
    //  * @private
    //  * @param {Vector} minerPosition 
    //  * @param {GridTile} tile 
    //  * @param {number} pixelRange2 
    //  * @returns {boolean}
    //  */
    // isTileInLaserRange(minerPosition, tile, pixelRange2) {
    //     return minerPosition.distanceSquared(tile.origin) <= pixelRange2;
    // }

    /**
     * @private
     * @param {Vector} startPoint 
     * @param {number} radians 
     * @param {number} pixelRange 
     * @returns {GridTile}
     */
    findFirstClosedTileOnLine(startPoint, radians, pixelRange) {

        let prevTile = null;
        for (let radius = Settings.tile.halfSize; radius <= pixelRange; radius += Settings.tile.halfSize) {

            const position = SkillUtil.projectPosition(startPoint, radius, radians, this.workingPosition);

            const projectedTile = this.getProjectedTile(prevTile, position);
            if (!projectedTile) {
                break;
            }

            // See if we've already checked this tile
            if (prevTile === projectedTile) {
                continue;
            }

            // Any tile touched by the drill should become visible.
            if (!projectedTile.lighting.everVisibleToCharacter) {
                projectedTile.markTileAsCurrentlyVisible();
            }

            // See if we've reached a closed tile
            if (!projectedTile.open) {
                return projectedTile;
            }

            prevTile = projectedTile; // no. projected tile is open
        }

        return prevTile;
    }    

    /**
     * @private
     * @param {Vector} start 
     * @param {Vector} end 
     * @returns {number} radians
     */
    calculateAngle(start, end) {
        // returns the angle between 2 points in radians
        // p1 = {x: 1, y: 2};
        // p2 = {x: 3, y: 4};
        return this.clamp(Math.atan2(end.y - start.y, end.x - start.x));
    }
}